import React from 'react';
import BaseWidget from './BaseWidget';
import TimerWidgetComponent from '../components/widgets/TimerWidgetComponent';
import {
  commonSerializedKeys,
  type SerializedTimerWidget,
  type WidgetType,
} from '.';
import {
  DEFAULT_DURATION,
  MILLIS,
  SECOND,
  timeInSeconds,
  elapsedTime,
} from '../utils/time';

export enum TimerStatus {
  Inactive,
  Active,
  Paused,
}

const DEFAULT_ATTRIBUTES = {
  status: TimerStatus.Inactive,
  startedAt: null,
  startDuration: DEFAULT_DURATION,
  exactDuration: DEFAULT_DURATION * MILLIS,
};

class TimerWidget extends BaseWidget {
  type: WidgetType = 'TimerWidget';

  status: TimerStatus;
  startedAt: number | null;
  startDuration: number;
  exactDuration: number;

  serializedKeys = [
    ...commonSerializedKeys,
    'status',
    'startedAt',
    'startDuration',
    'exactDuration',
  ];

  constructor(attributes: Partial<SerializedTimerWidget> = {}) {
    super({ ...DEFAULT_ATTRIBUTES, ...attributes });
  }

  render() {
    if (!this.controller.canDisplayTimer) {
      return null;
    }
    return <TimerWidgetComponent widget={this} key={this.id} />;
  }

  active(): boolean {
    if (!this.startedAt) return false;

    return this.status !== TimerStatus.Inactive;
  }

  paused(): boolean {
    return this.status === TimerStatus.Paused;
  }

  expired(): boolean {
    return this.remainingDuration() === 0;
  }

  warningTime(): boolean {
    if (this.dangerTime()) return false;

    return this.remainingDuration() <= this.startDuration * 0.25;
  }

  dangerTime(): boolean {
    return this.remainingDuration() <= 20 * SECOND;
  }

  remainingDuration(): number {
    if (!this.active()) {
      return -1; // Tombstone, shouldn't be called in that case
    }

    if (this.paused()) {
      return timeInSeconds(this.exactDuration);
    }

    const elapsed = elapsedTime(this.startedAt!);

    return Math.max(timeInSeconds(this.exactDuration - elapsed), 0);
  }
}

export default TimerWidget;
